/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import Modal from '@salesforce/design-system-react/components/modal';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import Button from '@salesforce/design-system-react/components/button';
import Spinner from '@salesforce/design-system-react/components/spinner';
import AutocompleteSingleSelectPicker from '../../Shared/AutocompleteSingleSelectPickerWithSearch';
import SnackBar from '../../Shared/SnackBar';
import useSnackBar from '../../../util/customHooks/useSnackBar';
import CartComponent from '../../Usage/UsageDetails/UsageTabs/Parts/CartComponent';
import './index.scss';
import { MAX_QUANTITY } from '../../../util/constants';

interface PriceLookupModalProps {
  isModalOpen: boolean;
  error: string | null;
  filteredAccountOptions: any[];
  filteredContractOptions: any[];
  loadingAccounts: boolean;
  loadingContracts: boolean;
  loadingContractProducts: boolean;
  selectedAccount: any;
  selectedContract: any;
  handleAccountSelect: (event: any, data: any) => void;
  handleContractSelect: (event: any, data: any) => void;
  setAccountSearchTerm: (value: string) => void;
  setContractSearchTerm: (value: string) => void;
  setProductSearchTerm: (params: { searchTerm: string; contract: any }) => void;
  loadingSubInventory: boolean;
  setSearchText: (value: string) => void;
  filteredProducts: any[];
  handleQuantityChange: (productId: string, quantity: number) => void;
  selectedProducts: any[];
  handleProductAdd: () => void;
  setSelectedAccount: (account: any) => void;
  setSelectedContract: (contract: any) => void;
  setIsModalOpen: (isOpen: boolean) => void;
  handleReset: () => void;
  setActiveTab: (tab: 'Customer' | 'Contract') => void;
  totalQuantity: number;
}

const PriceLookupModal: React.FC<PriceLookupModalProps> = ({
  isModalOpen,
  error,
  filteredAccountOptions,
  filteredContractOptions,
  loadingAccounts,
  loadingContracts,
  loadingContractProducts,
  selectedAccount,
  selectedContract,
  handleAccountSelect,
  handleContractSelect,
  setAccountSearchTerm,
  setContractSearchTerm,
  loadingSubInventory,
  setSearchText,
  filteredProducts,
  handleQuantityChange,
  selectedProducts,
  handleProductAdd,
  setSelectedAccount,
  setSelectedContract,
  setIsModalOpen,
  setProductSearchTerm,
  handleReset,
  setActiveTab,
  totalQuantity,
}) => {
  const { open, notification } = useSnackBar();
  const [inputValue, setInputValue] = useState('');
  const [showContractTab, setShowContractTab] = useState(false);
  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleAddProducts = () => {
    handleProductAdd();
    handleToggleModal();
  };
  const fetchItemsWrapper = (value: string): string => {
    if (filteredAccountOptions) {
      const existingOption = filteredAccountOptions.find(
        option => option.label.toLowerCase() === value.toLowerCase()
      );
      if (!existingOption) {
        setAccountSearchTerm(value);
      }
    }
    return '';
  };

  const fetchContractsWrapper = (value: string): string => {
    if (filteredContractOptions) {
      const existingOption = filteredContractOptions.find(
        option => option.label.toLowerCase() === value.toLowerCase()
      );
      if (!existingOption) {
        setContractSearchTerm(value);
      } else {
        handleReset();
      }
    }
    return '';
  };

  const handleAccountInputChange = (_event: any, { value }: { value: string }): void => {
    setInputValue(value);
    setSearchText(value);
  };

  const handleContractInputChange = (_event: any, { value }: { value: string }): void => {
    setInputValue(value);
    setProductSearchTerm({ searchTerm: value, contract: selectedContract });
  };

  const handleTabSelect = (tab: 'Customer' | 'Contract') => {
    setActiveTab(tab);
    setInputValue('');
    handleReset();
  };

  const renderInputIcon = () => (
    <InputIcon assistiveText={{ icon: 'Search' }} name="search" category="utility" />
  );

  return (
    <div>
      <SnackBar open={open} notification={notification} />
      <Modal
        isOpen={isModalOpen}
        onRequestClose={handleToggleModal}
        footer={[
          <div className="usage-modal-footer" key="footer">
            {(loadingAccounts || loadingSubInventory) && <Spinner size="small" />}
            <Button key="cancel" label="Cancel" onClick={handleToggleModal} />
            <Button
              key="add"
              label="Add Products"
              variant="brand"
              onClick={handleAddProducts}
              disabled={!selectedAccount || selectedProducts.length === 0}
            />
          </div>,
        ]}
        dismissOnClickOutside={false}
      >
        <div style={{ display: 'grid', padding: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Add Parts</div>
          <div style={{ textAlign: 'end' }}>
            <CartComponent count={totalQuantity} color="black" textColor="white" />
          </div>
        </div>
        <div style={{ padding: '0rem 0.75rem 0rem 0.75rem' }}>
          <Tabs id="tabs-example-default" style={{ border: 'none' }} onSelect={handleTabSelect}>
            <TabsPanel label="Customer">
              <div className="slds-p-around_medium">
                {error && <p className="error-message">{error}</p>}
                <div className="full-width_button-product" id="product-catalog">
                  <AutocompleteSingleSelectPicker
                    items={filteredAccountOptions || []}
                    selectedItem={selectedAccount ? [selectedAccount] : []}
                    setSelectedItem={selection => {
                      if (selection && selection.length > 0) {
                        handleAccountSelect(null, { selection });
                      } else {
                        setSelectedAccount(null);
                        setSearchText('');
                        setInputValue('');
                      }
                    }}
                    label=""
                    placeholder="Select Account"
                    withAddMenuButton={false}
                    fetchItems={fetchItemsWrapper}
                    filter
                    disabled={loadingAccounts}
                    name="accounts"
                    isLoading={loadingAccounts}
                    autoSelectValue={false}
                  />
                </div>
                <div className="catalog-input">
                  <Input
                    assistiveText={{ spinner: 'Field data is loading' }}
                    iconRight={renderInputIcon()}
                    value={inputValue}
                    onChange={handleAccountInputChange}
                    hasSpinner={loadingSubInventory}
                    id="unique-id-4"
                    placeholder="Search Product"
                  />
                </div>
                {selectedAccount && (
                  <section className="slds-p-around_large-1">
                    {filteredProducts.map((product: any) => (
                      <div className="greyBorderBottom product-info" key={product.productId}>
                        <div
                          style={{
                            padding: '5px',
                            display: 'grid',
                            gridTemplateColumns: '4fr 1fr',
                          }}
                        >
                          {`${product.productNumber} ${product.productdescription}`}
                        </div>
                        <div style={{ marginLeft: '80px' }}>
                          <Input
                            id="counter-input-3"
                            styleContainer={{ width: '130px' }}
                            styleInput={{ padding: '0px' }}
                            minValue={0}
                            maxValue={MAX_QUANTITY}
                            onChange={(
                              event: any,
                              data: { value: string; number: number }
                            ): void => {
                              handleQuantityChange(product.productId, data.number);
                            }}
                            value={
                              selectedProducts.find(item => item.productId === product.productId)
                                ?.quantity || 0
                            }
                            variant="counter"
                          />
                        </div>
                      </div>
                    ))}
                  </section>
                )}
              </div>
            </TabsPanel>
            {showContractTab && (
              <TabsPanel label="Contract">
                <section className="slds-p-around_medium">
                  {error && <p className="error-message">{error}</p>}
                  <div className="full-width_button-product" id="product-catalog">
                    <AutocompleteSingleSelectPicker
                      items={filteredContractOptions || []}
                      selectedItem={selectedContract ? [selectedContract] : []}
                      setSelectedItem={selection => {
                        if (selection && selection.length > 0) {
                          handleContractSelect(null, { selection });
                          setSelectedAccount(null);
                        } else {
                          setSelectedContract(null);
                          handleContractSelect(null, { selection: [] });
                          setInputValue('');
                          handleReset();
                        }
                      }}
                      label=""
                      placeholder="Select Contract"
                      withAddMenuButton={false}
                      fetchItems={fetchContractsWrapper}
                      filter
                      disabled={loadingContracts}
                      name="contracts"
                      isLoading={loadingContracts}
                    />
                  </div>
                  <div className="catalog-input">
                    <Input
                      assistiveText={{ spinner: 'Field data is loading' }}
                      iconRight={renderInputIcon()}
                      value={inputValue}
                      onChange={handleContractInputChange}
                      hasSpinner={loadingContracts}
                      id="unique-id-5"
                      placeholder="Search Product"
                    />
                  </div>
                  {selectedContract && (
                    <section className="slds-p-around_large-1">
                      {filteredProducts.map((product: any) => (
                        <div className="product-info" key={product.productId}>
                          <div
                            style={{
                              marginTop: '5px',
                            }}
                          >
                            {`${product.productNumber} ${product.productDescription}`}
                          </div>
                          <div style={{ marginLeft: '80px' }}>
                            <Input
                              id={`counter-input-${product.productId}`}
                              styleContainer={{ width: '130px' }}
                              styleInput={{ padding: '0px' }}
                              minValue={0}
                              maxValue={MAX_QUANTITY}
                              onChange={(
                                event: any,
                                data: { value: string; number: number }
                              ): void => {
                                handleQuantityChange(product.productId, data.number);
                              }}
                              value={
                                selectedProducts.find(item => item.productId === product.productId)
                                  ?.quantity || 0
                              }
                              variant="counter"
                            />
                          </div>
                        </div>
                      ))}
                    </section>
                  )}
                </section>
              </TabsPanel>
            )}
          </Tabs>
        </div>
      </Modal>
    </div>
  );
};

export default PriceLookupModal;
